import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { getDefaultUiLocale } from '../../../common/utilities/get-default-ui-language';
import { normalizeTranslationRegion, readPersistedUiState } from '../../../data/client-state/ui-state-persistence';

export type Dictionary = {
    [key: string]: string | Dictionary;
};

/**
 * A loader for ngx-translate which extends the HttpLoader functionality by stripping out any
 * values which are empty strings. This means that during development, translation keys which have
 * been extracted but not yet defined will fall back to the raw key text rather than displaying nothing.
 *
 * Orignally from https://github.com/ngx-translate/core/issues/662#issuecomment-377010232
 */
export class CustomHttpTranslationLoader implements TranslateLoader {
    constructor(
        private http: HttpClient,
        private prefix: string = '/assets/i18n/',
        private suffix: string = '.json',
    ) { }

    public getTranslation(lang: string): Observable<any> {
        return this.http
            .get(`${this.prefix}${lang}${this.suffix}`)
            .pipe(map((res: any) => this.process(res)));
    }

    private process(object: Dictionary): Dictionary {
        const newObject: Dictionary = {};

        for (const key in object) {
            if (object.hasOwnProperty(key)) {
                const value = object[key];
                if (typeof value !== 'string') {
                    newObject[key] = this.process(value);
                } else if (typeof value === 'string' && value === '') {
                    // do not copy empty strings
                } else {
                    newObject[key] = object[key];
                }
            }
        }

        return newObject;
    }
}

/**
 * Simplified cached version of CustomHttpTranslationLoader
 */
export class CachedCustomHttpTranslationLoader implements TranslateLoader {
    private cache = new Map<string, { data: any; timestamp: number }>();
    private loading = new Set<string>();

    constructor(
        private http: HttpClient,
        private localStorageService: LocalStorageService,
        private prefix: string = '/assets/i18n/',
        private suffix: string = '.json',
        private cacheTTL: number = 10 * 60 * 1000, // 10 minutes
        private enableLogging: boolean = false,
    ) {
        //this.log('Cache initialized with TTL:', Math.round(this.cacheTTL / 1000 / 60), 'minutes');
    }

    public getTranslation(lang: string): Observable<any> {
        const uiLocale = this.getUiLocale();
        const cacheKey = `phx-ui-lang-${lang}-${uiLocale ?? 'default'}`;

        // Check cache first
        const cached = this.cache.get(cacheKey);
        if (cached && this.isCacheValid(cached)) {
            this.log('Cache hit for', lang);
            return of(cached.data);
        }

        // Remove expired cache entry
        if (cached) {
            this.cache.delete(cacheKey);
            this.log('Cache expired for', lang);
        }

        // Check if already loading
        if (this.loading.has(cacheKey)) {
            this.log('Already loading', lang);
            // Return a new observable that waits for the cache to be populated
            return new Observable(subscriber => {
                const checkCache = () => {
                    const nowCached = this.cache.get(cacheKey);
                    if (nowCached) {
                        subscriber.next(nowCached.data);
                        subscriber.complete();
                    } else {
                        setTimeout(checkCache, 50); // Check again in 50ms
                    }
                };
                checkCache();
            });
        }

        // Start loading
        this.loading.add(cacheKey);
        this.log('Loading', lang);

        return this.loadTranslationResource(lang, uiLocale)
            .pipe(
                map((res: Dictionary) => this.process(res)),
                tap(data => {
                    // Cache the result
                    this.cache.set(cacheKey, {
                        data,
                        timestamp: Date.now()
                    });
                    this.loading.delete(cacheKey);
                    this.log('Cached', lang, '- total cached:', this.cache.size);
                }),
                tap({
                    error: () => {
                        this.loading.delete(cacheKey);
                        this.log('Error loading', lang);
                    }
                })
            );
    }

    private loadTranslationResource(lang: string, uiLocale: string | undefined): Observable<Dictionary> {
        const isSwissRegion = uiLocale?.toUpperCase() === 'CH';
        if (lang !== 'de' || !isSwissRegion) {
            return this.http.get<Dictionary>(`${this.prefix}${lang}${this.suffix}`);
        }
        return forkJoin({
            baseTranslation: this.http.get<Dictionary>(`${this.prefix}${lang}${this.suffix}`),
            tenantPatch: this.http.get<Dictionary>(`${this.prefix}ch.json.patch`).pipe(
                catchError(() => of({} as Dictionary)),
            ),
        }).pipe(
            map(({ baseTranslation, tenantPatch }) =>
                this.mergeTranslations(baseTranslation, tenantPatch),
            ),
        );
    }

    /**
     * Clear cache for specific language or all
     */
    public clearCache(lang?: string): void {
        if (lang) {
            const prefix = `phx-ui-lang-${lang}-`;
            for (const key of this.cache.keys()) {
                if (key.startsWith(prefix)) {
                    this.cache.delete(key);
                }
            }
            for (const key of this.loading.values()) {
                if (key.startsWith(prefix)) {
                    this.loading.delete(key);
                }
            }
            this.log('Cleared cache for', lang);
        } else {
            this.cache.clear();
            this.loading.clear();
            this.log('Cleared all cache');
        }
    }

    /**
     * Get simple cache info
     */
    public getCacheInfo(): { size: number; languages: string[] } {
        const languages = Array.from(this.cache.keys()).map(key => key.replace('phx-ui-lang-', ''));
        return { size: this.cache.size, languages };
    }

    private getUiLocale(): string | undefined {
        const { locale } = readPersistedUiState(this.localStorageService);
        return normalizeTranslationRegion(locale || getDefaultUiLocale());
    }

    private isCacheValid(cached: { timestamp: number }): boolean {
        return Date.now() - cached.timestamp < this.cacheTTL;
    }

    private log(...args: any[]): void {
        if (this.enableLogging) {
            console.log('[TranslationCache]', ...args);
        }
    }

    private process(object: Dictionary): Dictionary {
        const newObject: Dictionary = {};

        for (const key in object) {
            if (object.hasOwnProperty(key)) {
                const value = object[key];
                if (typeof value !== 'string') {
                    newObject[key] = this.process(value);
                } else if (typeof value === 'string' && value === '') {
                    // do not copy empty strings
                } else {
                    newObject[key] = object[key];
                }
            }
        }

        return newObject;
    }

    private mergeTranslations(baseTranslation: Dictionary, tenantPatch: Dictionary): Dictionary {
        const mergedTranslation: Dictionary = { ...baseTranslation };
        for (const key in tenantPatch) {
            if (!Object.prototype.hasOwnProperty.call(tenantPatch, key)) {
                continue;
            }
            const baseValue = mergedTranslation[key];
            const patchValue = tenantPatch[key];
            if (this.isDictionary(baseValue) && this.isDictionary(patchValue)) {
                mergedTranslation[key] = this.mergeTranslations(baseValue, patchValue);
                continue;
            }
            mergedTranslation[key] = patchValue;
        }
        return mergedTranslation;
    }

    private isDictionary(value: string | Dictionary | undefined): value is Dictionary {
        return typeof value === 'object' && value !== null;
    }
}
